import { HttpResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

import { SondermindHttpService } from '@sondermind/http-deprecated';
import { ServerResponse } from '@sondermind/utilities/models-http';
import { ProviderUser } from '@sondermind/utilities/models-user-provider';

// Can't just do Partial<ProviderUser> because we need to ensure the id property is present
export type PartialProviderWithId = Partial<Omit<ProviderUser, 'id'>> & { id: ProviderUser['id']; };

@Injectable({
  providedIn: 'root'
})
export class ProviderUserService {
  constructor(
    private http: SondermindHttpService
  ) {
  }

  load(user_id: any): Observable<ServerResponse<ProviderUser>> {
    return this.http
      .get<any>(['/users/:user_id.json', { user_id: user_id.toString() }], this.http.authRequestOptions())
      .pipe(
        map((res) => this.processLoad(res)),
        catchError((res) => this.http.handleErrorObject(res, 'user'))
      );
  }

  submitProfile(user_id: any): Observable<ServerResponse<ProviderUser>> {
    return this.http
      .patch<any>(['/users/:user_id/submit_profile.json', { user_id }], {}, this.http.authRequestOptions())
      .pipe(
        map((res) => this.processResponse(res)),
        catchError((res) => this.http.handleErrorObject(res, 'user'))
      );
  }

  updatePassword(user_id: any, password: string, password_confirmation: string): Observable<ServerResponse<ProviderUser>> {
    const params = {
      user: { password, password_confirmation }
    };

    return this.http
      .patch<any>(['/users/:user_id.json', { user_id }], params, this.http.authRequestOptions())
      .pipe(
        map((res) => this.processResponse(res)),
        catchError((res) => this.http.handleErrorObject(res, 'user'))
      );
  }

  update(user: PartialProviderWithId): Observable<ServerResponse<ProviderUser>> {
    const url = this.http.generateUrl('/users/:user_id.json', { user_id: user.id.toString() });
    const params = { user: this.generateParameters(user) };

    return this.http
      .patch<any>(url, params, this.http.authRequestOptions())
      .pipe(
        map((res) => this.processResponse(res)),
        catchError((res) => this.http.handleErrorObject(res, 'user'))
      );
  }

  updateAutoSaveSessionNotes(user_id: any, status: boolean): Observable<ServerResponse<ProviderUser>> {
    const params = { auto_save_session_notes: status };
    const options = this.http.authRequestOptions();

    return this.http
      .patch<any>(['/users/:user_id/update_auto_save_session_notes.json', { user_id }], params, options)
      .pipe(
        map((res) => this.processResponse(res)),
        catchError((res) => this.http.handleErrorObject(res, 'user'))
      );
  }

  resetInviteCode(user_id: string): Observable<ServerResponse<ProviderUser>> {
    const params = {
      user: { invite_code: null }
    };

    return this.http
      .patch<any>(['/users/:user_id.json', { user_id }], params, this.http.authRequestOptions())
      .pipe(
        map((res) => this.processResponse(res)),
        catchError((res) => this.http.handleErrorObject(res, 'user'))
      );
  }

  resendInviteCode(invite_token: string): Observable<any> {
    const options = this.http.noAuthRequestOptions();
    options.params = new HttpParams();
    options.params = options.params.set('invite_token', invite_token.toString());

    return this.http
      .get('/users/resend_invite_code.json', options);
  }

  private processLoad(res: HttpResponse<any>): ServerResponse<ProviderUser> {
    const server_response = new ServerResponse(res, 'user');
    const u = new ProviderUser();
    u.load_from_json(server_response.value);

    server_response.value = u;
    return server_response;
  }

  private processResponse(res: HttpResponse<any>): ServerResponse<ProviderUser> {
    const server_response = new ServerResponse(res, 'user');
    const u = new ProviderUser();
    u.load_from_json(server_response.value);
    server_response.value = u;
    return server_response;
  }

  private generateParameters(user: PartialProviderWithId): any {
    const result = {};

    result['first_name'] = user.firstName;
    result['middle_name'] = user.middleName;
    result['last_name'] = user.lastName;
    result['preferred_first_name'] = user.preferredFirstName;
    result['pronouns'] = user.pronouns;
    result['email'] = user.email;
    result['username'] = user.username;
    result['time_zone'] = user.timeZone;
    result['bio'] = user.bio;
    result['practice_name'] = user.practiceName;
    result['birth_date'] = user.birthDate;
    result['search_bio'] = user.searchBio;
    result['website'] = user.website;
    result['phone_number'] = user.phoneNumber;
    result['npi'] = user.npi;
    result['public_phone_number'] = user.publicPhoneNumber;
    result['upload_file_contents'] = user.uploadFileContents;
    result['upload_file_name'] = user.uploadFileName;
    result['cellphone_number'] = user.cellphoneNumber;
    result['subscription_sms'] = user.subscriptionSms;
    result['gender_id'] = user.genderId;
    result['practice_name'] = user.practiceName;
    result['smma_agreement'] = user.smmaAgreement;
    result['accepting_clients'] = user.acceptingClients;
    result['auto_save_session_notes'] = user.autoSaveSessionNotes;

    if (user.mailingAddress){
      result['mailing_address_attributes'] = {
        address_line1: user.mailingAddress.addressLine1,
        address_line2: user.mailingAddress.addressLine2,
        city: user.mailingAddress.city,
        state: user.mailingAddress.state,
        postal_code: user.mailingAddress.postalCode
      };
    }

    if (user.password != null && user.passwordConfirmation != null) {
      result['password'] = user.password;
      result['password_confirmation'] = user.passwordConfirmation;
    }

    return result;
  }
}
